import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Button, CircularProgress, Dialog, IconButton, TextField, Typography,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import InlineBanner from '../components/InlineBanner';
import WaSampleRowStepper from './WaSampleRowStepper';
import { getTemplateTextWithVariable } from '../../Whatsapp/Common';
import {
    clearWaTestResult, getWaPreview, sendWaTest, setWaPreviewOffset,
} from '../../../redux/reducers/smartSendWhatsAppSlice';
import { fillPreviewValues, unboundIndexes } from './templateShape';
import {
    MAX_TEST_PHONES, isValidWaPhone, loadTestRecipients, normalizePhone, saveTestRecipients,
} from './phone';
import { UseWaAutosave } from './useWaAutosave';
import useWaErrorText from './errorText';
import { WaTemplateItem, WaTestSendResultItem } from '../../../Models/DataSources/SmartSendWhatsApp';

// SmartSend part B — "שליחת בדיקה" for the WhatsApp tab.
//
// 🔴 A TEST SEND IS A REAL SEND (invariant 9): it goes out over the live platform, it is CHARGED
// like any other message, and there is no sandbox. That is why this dialog (a) shows the exact
// rendered text before the button, (b) caps the list at two numbers, and (c) states the cost in
// words. None of those is decoration.
//
// It is a SEPARATE component from `components/TestSendDialog.tsx` on purpose: that file is
// declared byte-identical by the contract (§1 / 14-W3 §4). It also calls back into
// `saveMapping`, which is why the e-mail tab's write lock has to be enforced in
// `SmartSendScreen` rather than inside the dialog (invariant 24(ג)).

interface Props {
    open: boolean;
    campaignId: number;
    onClose: () => void;
    autosave: UseWaAutosave;
}

const WhatsAppTestSendDialog: React.FC<Props> = ({ open, campaignId, onClose, autosave }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    // 09-channel-units — `campaignId` is the WhatsApp UNIT's own id (a WACampaignID) on every page
    // that opens this dialog, "both" included, so the test is always on channel 3 — the same unit
    // and channel the tab it was opened from saves to.
    const testChannel: number = 3;
    const { waErrorText, isEmptyState } = useWaErrorText();
    const isRTL = useSelector((s: any) => s.core && s.core.isRTL);
    const username = useSelector((s: any) => s.user?.username ?? null);

    const templates: WaTemplateItem[] = useSelector((s: any) => s.smartSendWa.waTemplates);
    const templateId: string | null = useSelector((s: any) => s.smartSendWa.whatsAppTemplateId);
    const legTemplate = useSelector((s: any) => s.smartSendWa.waMapping?.Template);
    const placeholderCount: number = useSelector((s: any) => s.smartSendWa.waPlaceholderCount ?? 0);
    const variableMap = useSelector((s: any) => s.smartSendWa.waVariableMap);
    const preview = useSelector((s: any) => s.smartSendWa.waPreview);
    const previewStatus = useSelector((s: any) => s.smartSendWa.previewStatus);
    const waDirty = useSelector((s: any) => s.smartSendWa.waDirty);
    const templatesStatus = useSelector((s: any) => s.smartSendWa.templatesStatus);
    const testResult: WaTestSendResultItem[] | null = useSelector((s: any) => s.smartSendWa.waTestResult);
    // 🔴 The dialog's second outcome channel, and the reason it needed one: every route-level
    // refusal of `PUT WhatsApp/TestSend` returns with no `Data`, so the per-number list stayed
    // empty and its non-empty gate rendered NOTHING. A refusal was indistinguishable from "the
    // click did nothing" — on a dialog whose own banner says each test message is charged.
    const testError = useSelector((s: any) => s.smartSendWa.waTestError);
    const previewError = useSelector((s: any) => s.smartSendWa.previewError);

    const [phones, setPhones] = useState<string[]>([]);
    const [sending, setSending] = useState(false);

    // Opening the dialog: restore the remembered numbers and clear the previous run.
    useEffect(() => {
        if (!open) return;
        setPhones(loadTestRecipients(username));
        dispatch(clearWaTestResult());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, campaignId]);

    // 🔴 THIS DIALOG FETCHES ITS OWN ROW, AND `preview.offset` IS A DEPENDENCY.
    //
    // It used to fetch once, on `[open, campaignId]`, while its stepper dispatched
    // `setWaPreviewOffset` — and NOTHING in this dialog reacted to that. The label moved, the
    // row did not. The only effect in the application that watches `preview.offset` lives in
    // `WhatsAppPhonePreview`, a SIBLING inside the mapping tab, and that one returns early on
    // `!savedMapping`. So this dialog was correct only as a side effect of another component's
    // effect, under a guard that belongs to that component's concern and not to this one.
    //
    // What that buys, when the coupling breaks: "שורה לדוגמה 3 מתוך 5" on screen, row 1's
    // `RowID` in the request, and a REAL, CHARGED WhatsApp message to the wrong person's data.
    // A dialog that spends money does not get to borrow its correctness from a sibling.
    useEffect(() => {
        if (!open || !campaignId) return;
        // ⚠️ AND IT DOES NOT ASK TWICE FOR A ROW IT ALREADY HAS. Opening the dialog on the same
        // offset the phone preview has already loaded used to fire a second, identical GET —
        // which `condition` cannot stop, because nothing is in flight by then. That request's
        // `pending` resets `previewStatus` and `previewError`, so it ERASED a banner the sibling
        // was showing; and if the answer were `406 NO_ROWS_AT_OFFSET` it would reset the offset
        // to 0 — the row jumping under the operator merely because they opened a dialog.
        //
        // A stepper move still refetches: moving the offset is exactly what makes `rowOffset`
        // disagree with it.
        if (preview.rowOffset === preview.offset && preview.row) return;
        dispatch(getWaPreview({ campaignId, offset: preview.offset, channel: testChannel }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, campaignId, preview.offset]);

    // ONE source for the body — `GET WhatsApp/Templates`. The old `legTemplate?.BodyText` arm
    // read a field the server does not send (`WaMappingTemplate` has nine, and that is not one
    // of them), so it always collapsed to `''` and showed an empty message before a charged
    // test send. See the same note in WhatsAppPhonePreview.
    const bodyText: string = useMemo(() => {
        const tpl = templates.find((x) => x.TemplateId === templateId);
        return tpl?.BodyText ?? '';
    }, [templates, templateId]);

    const templatesLoaded = templatesStatus === 'succeeded' || templatesStatus === 'failed';
    const templateGone = !!templateId && templatesLoaded
        && !templates.some((x) => x.TemplateId === templateId);
    const legSaysUnusable = !!legTemplate
        && (legTemplate.TemplateIsDeleted === true
            || (legTemplate.TemplateStatusId != null && legTemplate.TemplateStatusId !== 3));
    const messageUnavailable = templateGone || legSaysUnusable;

    const values = useMemo(
        () => fillPreviewValues(placeholderCount, preview.row?.Values),
        [placeholderCount, preview.row],
    );
    const renderedText = useMemo(
        () => getTemplateTextWithVariable(bodyText, values as any),
        [bodyText, values],
    );

    const unbound = unboundIndexes(placeholderCount, variableMap);
    const candidateCount = Math.min(preview.row?.CandidateCount ?? 0, 5);

    // 🔴 AND THE GUARD THAT MAKES THE MISMATCH UNSENDABLE, not merely unlikely.
    //
    // The effect above keeps the row in step with the stepper; this makes the failure SAFE if it
    // ever stops doing so. `rowOffset` is the offset the held row was actually fetched at
    // (stamped in the slice from the request), so when it disagrees with the offset the label is
    // showing, there is no row this dialog is entitled to send — `rowId` is null, `doSend`'s
    // existing `rowId == null` guard returns, and the button is already disabled on the same
    // condition. The worst case degrades to a disabled button, never to a charged send of a row
    // the operator did not choose.
    const rowMatchesOffset = preview.rowOffset === preview.offset;
    const rowId = rowMatchesOffset ? (preview.row?.RowID ?? null) : null;

    // 🔴 AND THE SAME DISAGREEMENT DRIVES WHAT THE OPERATOR SEES — not just what may be sent.
    //
    // The gate above made a mismatched row unsendable, and that created a window it did not
    // describe: `setWaPreviewOffset` lands first, so for the whole HTTP round trip the label
    // said "שורה לדוגמה 2 מתוך 5" while the box underneath still held row 1's text, with the
    // stepper disabled and the send button dead — no spinner, no banner, nothing. The worst case
    // had improved from "wrong send" to "dead button with no explanation", but silence on a
    // dialog that spends money is its own defect.
    //
    // `awaitingRow` is that window, and it is deliberately NOT `previewStatus === 'loading'`:
    // the status is global to the slice, so the sibling preview's own refresh would blank this
    // dialog for no reason. The question here is strictly "is the row I am holding the row my
    // label claims", and only this dialog's own stepper can make the answer no.
    const awaitingRow = !rowMatchesOffset && previewStatus !== 'failed';

    // 🔴 AND THE TEXT IS GATED ON THE MATCH ITSELF, NOT ON `awaitingRow`.
    //
    // `awaitingRow` excludes the failed state — correctly, because nothing is on its way any
    // more and a spinner there would be a lie of its own. But the row/rowOffset from the
    // PREVIOUS offset survive a failure: the slice only clears them for the two 406 empty codes
    // (`isPreviewEmptyCode`), so a real refusal — `WA_INTERNAL`, `WA_ROW_NOT_FOUND`, a dropped
    // request — left the old text standing under the new label. The same lie C-1 closed, one
    // window over.
    //
    // Three states, and each says something true:
    //   · mismatch, still fetching  → a spinner (the row the label names is coming)
    //   · mismatch, fetch failed    → NOTHING here; the banner above already names the reason
    //   · match                     → the text, which is then genuinely this row's
    const showRowText = rowMatchesOffset;

    const setPhone = (i: number, v: string) => {
        const next = phones.slice();
        next[i] = v;
        setPhones(next);
    };

    const cleaned = phones.map(normalizePhone).filter((p) => p !== '');
    const allValid = cleaned.length > 0 && cleaned.every(isValidWaPhone);

    const doSend = async () => {
        if (!allValid || rowId == null) return;
        setSending(true);
        // The server renders from the SAVED mapping, so an unsaved edit would produce a test
        // message that is not the message on screen. Flush first — through the same choke point
        // that the lock disables, so a locked tab cannot write from here either.
        autosave.cancelPending();
        if (waDirty) await autosave.saveWaMapping();
        await dispatch(sendWaTest({ campaignId, rowId, phones: cleaned, channel: testChannel }) as any);
        saveTestRecipients(username, cleaned);
        setSending(false);
    };

    return (
        <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                <Typography variant="h6">{t('DataSources.send.whatsapp.testSend.title')}</Typography>
                <IconButton size="small" onClick={onClose} disabled={sending} aria-label={t('DataSources.send.close')}>
                    <Close fontSize="small" />
                </IconButton>
            </Box>

            <Box style={{ padding: 24 }}>
                {/* 🔴 Every test message is billed exactly like a production message. */}
                <InlineBanner
                    severity="warning"
                    size="lg"
                    role="status"
                    title={t('DataSources.send.whatsapp.testSend.cost')}
                />

                <Typography variant="subtitle2" style={{ fontWeight: 600, marginTop: 8 }}>
                    {t('DataSources.send.whatsapp.testSend.pickRow')}
                </Typography>
                <WaSampleRowStepper
                    offset={preview.offset}
                    count={candidateCount}
                    unboundCount={unbound.length}
                    disabled={previewStatus === 'loading' || sending}
                    onChange={(o) => dispatch(setWaPreviewOffset(o))}
                />

                {/* A refused preview — the sample row this dialog is about to send. */}
                {previewStatus === 'failed' && (
                    <Box style={{ marginTop: 8 }}>
                        <InlineBanner
                            severity={isEmptyState(previewError) ? 'info' : 'warning'}
                            role="status"
                            title={waErrorText(previewError)}
                        />
                    </Box>
                )}

                {/* What the recipient will actually see — shown BEFORE the send button, never
                    after (invariant 9: "הטקסט מוצג לפני"). When the template is no longer
                    approved there is no text to show, and an empty grey box would read as an
                    empty message rather than as a missing template. */}
                {awaitingRow ? (
                    /* A spinner, not a sentence: every string on this screen has to exist in
                       §7's key list, and "loading" is not one of them — adding it would fail the
                       i18n self-test in direction (b). A spinner needs no translation and says
                       the one thing that is true: the row the label names is on its way. */
                    <Box style={{ marginTop: 12, display: 'flex', justifyContent: 'center', padding: 12 }}>
                        <CircularProgress size={22} />
                    </Box>
                ) : !showRowText ? (
                    /* The fetch failed and what we hold belongs to another row. The warning
                       banner above carries the reason; printing the previous row's message
                       under this row's number would be the defect this gate exists to stop. */
                    null
                ) : messageUnavailable ? (
                    <Box style={{ marginTop: 12 }}>
                        <InlineBanner
                            severity="error"
                            role="alert"
                            title={t('DataSources.send.whatsapp.error.WA_TEMPLATE_NOT_APPROVED')}
                        />
                    </Box>
                ) : (
                    <Box style={{
                        marginTop: 12, padding: 12, borderRadius: 8,
                        background: 'var(--pls-d-surface-2, #f5f5f5)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                    }}>
                        <Typography variant="body2">{renderedText}</Typography>
                    </Box>
                )}

                <Typography variant="subtitle2" style={{ fontWeight: 600, marginTop: 16 }}>
                    {t('DataSources.send.whatsapp.testSend.phones', { max: MAX_TEST_PHONES.toLocaleString() })}
                </Typography>
                <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                    {Array.from({ length: MAX_TEST_PHONES }).map((_, i) => {
                        const raw = phones[i] ?? '';
                        const invalid = raw.trim() !== '' && !isValidWaPhone(raw);
                        return (
                            <TextField
                                // eslint-disable-next-line react/no-array-index-key
                                key={i}
                                variant="outlined"
                                size="small"
                                value={raw}
                                disabled={sending}
                                error={invalid}
                                helperText={invalid ? t('DataSources.send.whatsapp.testSend.phoneInvalid') : ''}
                                onChange={(e) => setPhone(i, e.target.value)}
                                inputProps={{
                                    'aria-label': `${t('DataSources.send.whatsapp.testSend.phones', { max: MAX_TEST_PHONES.toLocaleString() })} ${i + 1}`,
                                    dir: 'ltr',
                                }}
                            />
                        );
                    })}
                </Box>

                {/* 🔴 THE REFUSAL SURFACE. Rendered from `waTestError`, which the slice writes
                    on every non-2xx AND on a transport failure — the two cases that previously
                    left this dialog completely blank. */}
                {testError && (
                    <Box style={{ marginTop: 16 }}>
                        <InlineBanner
                            severity="error"
                            role="alert"
                            size="lg"
                            title={testError === 'SEND_ALREADY_SENT'
                                // 12-fix-wave 7.4(g) / 7.10 — a test send of a closed send: "send closed".
                                ? t('DataSources.send.errors.sendClosed')
                                : waErrorText(testError)}
                        />
                    </Box>
                )}

                {testResult && testResult.length > 0 && (
                    <Box style={{ marginTop: 16 }}>
                        {testResult.map((r) => (
                            <Typography key={r.Phone} variant="body2">
                                {t('DataSources.send.whatsapp.testSend.result', {
                                    phone: r.Phone,
                                    // 🔴 PER-NUMBER RESULT, AND THE ONE PLACE THE GATE WAS MISSED.
                                    // Code 0 is the success row; anything else carries a `Message`
                                    // — but NOT necessarily a §4 one. This is the route whose own
                                    // refusal codes include `DATA_INCORRECT` and
                                    // `CAMPAIGN_NOT_FOUND` (see the `sendWaTest` cases in the
                                    // slice): platform-wide codes that are deliberately NOT in
                                    // `WA_MESSAGES` and therefore have no `error.*` text — adding
                                    // them would fail the i18n self-test in direction (b), since
                                    // §7 does not list them.
                                    //
                                    // A bare `t()` printed the KEY for exactly those:
                                    // "DataSources.send.whatsapp.error.DATA_INCORRECT", as the
                                    // outcome of a message that was really sent and really
                                    // charged. `waErrorText` falls back to `WA_INTERNAL`
                                    // ("שגיאה פנימית … פנו לתמיכה"), which is both true and
                                    // actionable for a code this build does not know.
                                    status: r.Code === 0
                                        ? t('DataSources.send.whatsapp.receipt.sent')
                                        : waErrorText(r.Message),
                                })}
                            </Typography>
                        ))}
                    </Box>
                )}
            </Box>

            <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', borderTop: '1px solid var(--pls-d-border, #e0e0e0)' }}>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={sending || !allValid || rowId == null || messageUnavailable}
                    onClick={doSend}
                >
                    {sending
                        ? <CircularProgress size={18} color="inherit" />
                        : t('DataSources.send.whatsapp.testSend.send', { n: cleaned.length.toLocaleString() })}
                </Button>
                <Typography variant="caption" color="textSecondary">
                    {t('DataSources.send.whatsapp.testSend.activeTabHint')}
                </Typography>
                <Box style={{ flex: 1 }} />
                <Button onClick={onClose} disabled={sending}>{t('DataSources.send.cancel')}</Button>
            </Box>
        </Dialog>
    );
};

export default WhatsAppTestSendDialog;
